import {Grid} from '@mui/material';
import React, {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {user} from '../../../store/reducers/user';
import MessageBox from '../../../util/messageBox';
import uuidv4 from 'uuid/v4';
import {nextWorkflowStep, setWorkflow} from '../../../store/reducers/workflow';
import Notes from '../../../util/notes';
import Formatter from '../../../util/formatter';
import AgyleDialog from '../../../components/modal/agyleDialog';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import {SEPSIS_TOOL_TYPE} from '../forms/formsUtil';
import {PATIENT_AGE_TYPE} from '../../../util/patientContextUtil';
import PaedsSepsisTool from './components/paedsSepsisTool';
import AdultSepsisTool from './components/adultSepsisTool';
import useOpenState from '../../../hooks/useOpenState';
import MiniFormDialog from '../../../components/modal/miniFormDialog';
import {setBlockExitForm} from '../../../store/reducers/patientContext';
import {WORKFLOW_OPTIONS} from '../../../util/workflowUtil';

export const useSepsisStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.paperDarker,
    },
    dialogTitle: {
        textAlign: 'center',
        padding: '10px',
    },
    buttonCancel: {
        background: '#fff',
        width: '100px',
        color: '#000',
        '&:hover': {
            background: '#fff',
        },
    },
    buttonReject: {
        width: '300px',
    },
    buttonSave: {
        backgroundColor: '#a61c00',
        width: '100%',
        color: '#fff',
        height: 'auto !important',
        borderRadius: 6,
        '&:hover': {
            backgroundColor: '#a61c00',
            color: '#fff',
        },
    },
    anywayButtonSave: {
        backgroundColor: '#ffb74d',
        width: '100%',
        color: '#000',
        height: 'auto !important',
        borderRadius: 6,
        '&:hover': {
            backgroundColor: '#ffb74d',
            color: '#000',
        },
    },
    blink: {
        border: '2px solid #fff',
        animation: 'blink 1s',
        animationIterationCount: 'infinite',
        borderRadius: '5px',
        padding: '2px 3px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    category: {
        padding: '5px',
        fontWeight: 'bold',
        marginBottom: '10px',
    },
    section: {
        marginBottom: '15px',
    },
    checkboxLabel: {
        // background: theme.palette.background.paper,
        paddingRight: '5px',
        margin: '2px',
        minWidth: '100%',
    },
    sectionBox: {
        minHeight: 42,
        textAlign: 'left',
        paddingTop: 6,
    },
    sectionBoxAdult: {
        minHeight: 55,
        height: '100%',
        textAlign: 'left',
    },
    sectionSource: {
        textAlign: 'center',
        padding: 6,
        fontWeight: 'bold',
        fontSize: 11,
        color: '#dd7e6b',
    },
    input: {
        width: '100%',
        fontSize: '15px',
        backgroundColor: theme.palette.background.paper,
        height: 42,
    },
    textInput: {
        height: 9,
        fontSize: 14,
    },
    divider: {
        height: '1px',
        background: '#fff',
        marginBottom: '10px',
        marginTop: '10px',
    },
    smallFont: {
        fontSize: 'small',
        margin: '5px',
        '&.red': {
            color: theme.palette.mode === 'light' ? '#e24528' : '#dd7e6b',
        },
    },
    infoBox: {
        background: '#cfe2f3',
        color: 'black',
        fontSize: '12px',
        fontWeight: 'bold',
        textAlign: 'center',
        margin: '10px',
    },
    newObsWarning: {
        color: '#434343',
        fontSize: 'large',
        textAlign: 'center',
        fontWeight: 'bold',
        background: '#f1c232',
        marginBottom: 5,
        padding: 10,
    },
    footer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
}));

// Main screen
const SepsisToolForm = (props) => {
    const classes = useSepsisStyles();
    const {t} = useTranslation();

    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    // Opener
    const [isRejectModalOpen, openRejectModal, closeRejectModal] = useOpenState(false, true);
    const [isStartAnywayOpen, openStartAnyway, closeStartAnyway] = useOpenState(false, true);
    const [isSubmitOpen, openSubmit, closeSubmit] = useOpenState(false, true);

    // Main data
    const [sepsis, setSepsis] = useState(props.selectedHistoryForm || {});
    const [draft, setDraft] = useState(false);
    const [lowRisk, setLowRisk] = useState(false);
    const [moderateRisk, setModerateRisk] = useState(false);
    const [reason, setReason] = useState('');
    const [submitData, setSubmitData] = useState(null);

    // Check for showing save button
    const check = props.selectedHistoryForm === null || (props.selectedHistoryForm && props.selectedHistoryForm.status === SEPSIS_TOOL_TYPE.IN_COMPLETED);
    const [peadsCheck, setPeadsCheck] = useState(false);

    useEffect(() => {
        if (props.onHideSubmit) {
            props.onHideSubmit(true);
        }
    }, []);

    useEffect(() => {
        if (props.disableSave || (sepsis.status !== SEPSIS_TOOL_TYPE.NEW && sepsis.status !== SEPSIS_TOOL_TYPE.IN_COMPLETED)) {
            dispatch(setBlockExitForm(false));
        }
    }, [props.disableSave, sepsis]);

    const prepareData = (flag) => {
        let data = {
            ...sepsis,
            __id: sepsis.__id ? sepsis.__id : uuidv4() + '',
            ver: '1.1',
            date: moment().toISOString(),
            by: userFullName,
            login: userObject?.userName,
            attendanceId: props.patient?.admission?.__id,
        };
        if (flag) {
            data = {...data, status: SEPSIS_TOOL_TYPE.IN_COMPLETED};
        } else {
            data = {...data, status: SEPSIS_TOOL_TYPE.COMPLETED};
        }
        return data;
    };

    // Build a note
    const saveNotes = (sepsis) => {
        return Notes.getSepsisToolNote(sepsis);
    };

    const createObjAndNote = (sepsisForm) => {
        return {
            form: sepsisForm,
            note: saveNotes(sepsisForm),
            path: 'sepsisTool',
            oldList: [],
        };
    };
    const dispatch = useDispatch();

    const submit = (event, flag) => {
        event.preventDefault();
        const dataToSend = prepareData(flag);
        setSubmitData({
            data: dataToSend,
            flag: flag,
        });
        openSubmit();
    };

    const saveData = () => {
        closeSubmit();
        props.onChange(createObjAndNote(submitData.data));
        dispatch(setBlockExitForm(false));
        setSubmitData(null);
        if (!submitData.flag) {
            dispatch(setWorkflow({type: WORKFLOW_OPTIONS.SEPSIS_PATHWAY}));
        }
    };

    const reject = () => {
        if (reason !== '') {
            closeRejectModal();
            let data = prepareData();

            data = {
                ...data,
                status: SEPSIS_TOOL_TYPE.NOT_REQUIRED,
                reason: reason,
            };
            props.onChange(createObjAndNote(data));
            dispatch(nextWorkflowStep());
        }
    };

    const startAnyway = () => {
        if (reason !== '') {
            closeStartAnyway();
            let data = prepareData();

            data = {
                ...data,
                status: SEPSIS_TOOL_TYPE.COMPLETED_NOT_REQUIRED,
                reason: reason,
            };
            props.onChange(createObjAndNote(data));
            dispatch(setWorkflow({type: WORKFLOW_OPTIONS.SEPSIS_PATHWAY}));
        }
    };

    const saveButtonText = () => {
        if (!peadsCheck && props.patientAgeType === PATIENT_AGE_TYPE.PAEDS) {
            if (sepsis.status === SEPSIS_TOOL_TYPE.NEW) {
                return (
                    <AgyleButton
                        className={classes.anywayButtonSave}
                        onClick={openStartAnyway}
                        fullWidth
                    >
                        {t('START SEPSIS 6 PATHWAY')}
                    </AgyleButton>
                );
            }
        }
        if (check) {
            if (draft) {
                return (
                    <AgyleButton
                        className={`${classes.buttonSave} ${classes.blink}`}
                        onClick={(event) => submit(event, true)}
                        variant="text"
                        width="inherit"
                        fullWidth
                    >
                        <div style={{padding: 5}}>
                            {t('SAVE DRAFT')} <br/>
                            {t('COMPLETE THIS FORM AFTER BLOOD TEST RESULT, SENIOR DOCTOR REVIEW AND CLINICIAN ATTENDED')}
                            < br/>
                            {t('This is time critical, immediate action is required.')}
                        </div>
                    </AgyleButton>
                );
            } else if (lowRisk) {
                return (
                    <AgyleButton
                        className={`${classes.anywayButtonSave} anyway`}
                        onClick={openStartAnyway}
                        variant="text"
                        width="inherit"
                        fullWidth
                    >
                        {t('START SEPSIS 6 PATHWAY')}
                    </AgyleButton>
                );
            } else if (moderateRisk) {
                return (
                    <AgyleButton
                        className={classes.anywayButtonSave}
                        onClick={(event) => submit(event, false)}
                        variant="text"
                        width="inherit"
                        fullWidth
                    >
                        <div style={{padding: 5}}>
                            {t('START SEPSIS 6 PATHWAY NOW')} < br/>
                            {t('This is time critical, immediate action is required.')}
                        </div>
                    </AgyleButton>
                );
            } else {
                return (
                    <AgyleButton
                        className={classes.buttonSave}
                        onClick={(event) => submit(event, false)}
                        variant="text"
                        width="inherit"
                        fullWidth
                    >
                        <div style={{padding: 5}}>
                            {t('RED FLAG SEPSIS. START SEPSIS 6 PATHWAY NOW')} < br/>
                            {t('This is time critical, immediate action is required.')}
                        </div>
                    </AgyleButton>
                );
            }
        }
    };

    const selectedSepsisTool = () => {
        if (sepsis?.type === 'paeds' || props.patientAgeType === PATIENT_AGE_TYPE.PAEDS) {
            return (
                <PaedsSepsisTool
                    data={sepsis}
                    save={setSepsis}
                    patient={props.patient}
                    buttonShow={setPeadsCheck}
                />
            );
        } else {
            return (
                <AdultSepsisTool
                    data={sepsis}
                    save={setSepsis}
                    patient={props.patient}
                    draft={setDraft}
                    lowRisk={setLowRisk}
                    moderateRisk={setModerateRisk}
                />
            );
        }
    };

    const footer = () => {
        return (
            <div className={classes.footer}>
                <div style={{width: '100%'}}>
                    {saveButtonText()}
                </div>

                <Grid container spacing={2} direction="row" justifyContent="space-between">
                    {check &&
                        <Grid item justifyContent="center" xs={3}>
                            <AgyleButton
                                className={classes.buttonReject}
                                onClick={openRejectModal}
                                variant={'contained'}
                                color="primary"
                            >
                                {t('Sepsis Pathway Not Required')}
                            </AgyleButton>
                        </Grid>
                    }
                </Grid>
            </div>
        );
    };

    return (
        <>
            <div>
                {selectedSepsisTool()}
            </div>

            <div>
                {footer()}
            </div>

            {/* Reject Dialog */}
            <MiniFormDialog
                title={t('Please provide a reason for not requiring the Sepsis Pathway')}
                fullWidth
                open={isRejectModalOpen}
                onClose={closeRejectModal}
                onSave={reject}
                noPadding
            >
                <div style={{padding: 10}}>
                    <AgyleTextField
                        label={t('Reason')}
                        fullWidth
                        required
                        error={reason === ''}
                        style={{marginTop: 10}}
                        onChange={event => setReason(event.target.value)}
                        value={reason}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </div>
            </MiniFormDialog>

            {/* Start Anyway Sepsis pathway Dialog */}
            <AgyleDialog
                title={t('Please provide a reason for starting Sepsis Pathway')}
                open={isStartAnywayOpen}
                width="md"
                fullWidth
                onClose={closeStartAnyway}
                onSave={startAnyway}
                noPadding
            >
                <>
                    <div style={{padding: 10}}>
                        <AgyleTextField
                            autoFocus
                            label={t('Reason')}
                            fullWidth
                            variant="outlined"
                            required
                            error={reason === ''}
                            value={reason}
                            onChange={event => setReason(event.target.value)}
                        />
                    </div>
                </>
            </AgyleDialog>

            <MessageBox
                open={isSubmitOpen}
                onClose={closeSubmit}
                title={t('Confirmation')}
                message={t('Are you sure you want to save?')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={saveData}
            />
        </>
    );
};

export default SepsisToolForm;